import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Logger } from 'utils/Logger';
import { apiClient } from 'utils/ApiClient';

// Types for WhatsApp config
export interface WhatsAppConfig {
  isActive: boolean;
  qrCodeString?: string | null;
  qrCodeTimestamp?: string | null;
  lastUpdated?: string | null;
  status: string;
  phoneNumber?: string | null;
}

// Types for Email config
export interface EmailConfig {
  lastUpdated?: string | null;
  providerType?: string;
  isConfigured?: boolean;
}

// Types for GSM Worker
export interface GSMWorker {
  workerID: string;
  phoneNumber: string;
  lastStatusUpdate?: string | null;
  lastUpdateQuota?: string | null;
  signalStrength?: string | null;
  operatorName?: string | null;
  networkState?: string | null;
  messageTrackerCount?: number;
  messageCapacityPerDay?: number;
}

// Types for GSM Worker Pool
export interface GSMWorkerPoolConfig {
  poolName: string;
  location?: string;
  countryCode?: string;
  poolType?: string;
  status?: string;
  updatedAt?: string | null;
  totalWorkers?: number;
  totalQueueItems?: number;
  queueSummary?: {
    scheduled: number;
    inProgress: number;
    completed: number;
    failed: number;
    skipped: number;
  };
  workers?: GSMWorker[];
  error?: string;
}

// Combined Bot Status
export interface BotStatus {
  whatsapp: WhatsAppConfig | null;
  email: EmailConfig | null;
  gsmWorkerPool: GSMWorkerPoolConfig | null;
}

export interface BotStatusState {
  botStatus: BotStatus | null;
  loading: boolean;
  error: string | null;
  lastFetched: number | null;
  // Deduced WhatsApp status for UI
  whatsappDeducedStatus: 'OFFLINE' | 'ONLINE' | 'QRCODE' | 'BUSY';
  isQRCodeOutdated: boolean;
  // Deduced Email status for UI
  emailDeducedStatus: 'OFFLINE' | 'ONLINE';
}

const initialState: BotStatusState = {
  botStatus: null,
  loading: false,
  error: null,
  lastFetched: null,
  whatsappDeducedStatus: 'OFFLINE',
  isQRCodeOutdated: false,
  emailDeducedStatus: 'OFFLINE',
};

// Deduce WhatsApp status based on business rules
const deduceWhatsAppStatus = (config: WhatsAppConfig | null): { status: 'OFFLINE' | 'ONLINE' | 'QRCODE' | 'BUSY'; isOutdated: boolean } => {
  if (!config) {
    return { status: 'OFFLINE', isOutdated: false };
  }

  const qrCodeString = config.qrCodeString;
  const phoneNumber = config.phoneNumber;
  const qrCodeTimestamp = config.qrCodeTimestamp;
  const lastUpdated = config.lastUpdated;
  const serverStatus = config.status?.toLowerCase() || '';

  // Helper to check if a timestamp is older than 1 minute
  const isOlderThanOneMinute = (timestamp?: string | null) => {
    if (!timestamp) return true;
    const now = Date.now();
    const ts = new Date(timestamp).getTime();
    return now - ts > 60 * 1000;
  };

  // STATUS = QRCODE (green)
  if (
    qrCodeString && qrCodeString.trim() !== '' &&
    !isOlderThanOneMinute(qrCodeTimestamp) &&
    (!phoneNumber || phoneNumber.trim() === '')
  ) {
    return { status: 'QRCODE', isOutdated: false };
  }

  // STATUS = OFFLINE (red)
  if (
    isOlderThanOneMinute(qrCodeTimestamp) &&
    isOlderThanOneMinute(lastUpdated)
  ) {
    return { status: 'OFFLINE', isOutdated: true };
  }

  // STATUS = BUSY
  if (
    phoneNumber && phoneNumber.trim() !== '' &&
    !isOlderThanOneMinute(lastUpdated) &&
    serverStatus === 'busy'
  ) {
    return { status: 'BUSY', isOutdated: false };
  }

  // STATUS = ONLINE (green)
  if (
    phoneNumber && phoneNumber.trim() !== '' &&
    !isOlderThanOneMinute(lastUpdated)
  ) {
    return { status: 'ONLINE', isOutdated: false };
  }

  // Default fallback
  return { status: 'OFFLINE', isOutdated: true };
};

// Deduce Email status based on lastUpdated timestamp (ONLINE if within 30 seconds)
const deduceEmailStatus = (config: EmailConfig | null): 'OFFLINE' | 'ONLINE' => {
  if (!config || !config.lastUpdated) {
    return 'OFFLINE';
  }

  const now = Date.now();
  const lastUpdatedTs = new Date(config.lastUpdated).getTime();
  const diffInSeconds = (now - lastUpdatedTs) / 1000;

  // If last updated within 30 seconds, consider it ONLINE
  if (diffInSeconds <= 30) {
    return 'ONLINE';
  }

  return 'OFFLINE';
};

// Async thunk to fetch Bot Status (combined WhatsApp, Email, GSM Worker Pool)
export const fetchBotStatus = createAsyncThunk(
  'botStatus/fetchBotStatus',
  async (companyId: string, { rejectWithValue }) => {
    try {
      Logger.info("botStatusSlice.ts : fetchBotStatus()", `Fetching bot status for companyId: ${companyId}`);
      
      const data = await apiClient.get(`/configuration/${companyId}/botStatus`);
      const statusData = data.data || null;
      
      Logger.info("botStatusSlice.ts : fetchBotStatus()", `Fetched bot status: ${JSON.stringify(statusData)}`);
      
      return statusData as BotStatus;
    } catch (error) {
      Logger.error("botStatusSlice.ts : fetchBotStatus()", `Failed to fetch bot status: ${error}`);
      return rejectWithValue(error instanceof Error ? error.message : String(error));
    }
  }
);

// Async thunk to update WhatsApp configuration (kept for backward compatibility)
export const updateWhatsAppConfig = createAsyncThunk(
  'botStatus/updateWhatsAppConfig',
  async ({ companyId, config }: { companyId: string; config: Partial<WhatsAppConfig> }, { rejectWithValue }) => {
    try {
      Logger.info("botStatusSlice.ts : updateWhatsAppConfig()", `Updating WhatsApp config for companyId: ${companyId}`);
      
      const data = await apiClient.put(`/configuration/${companyId}/whatsapp`, config);
      const configData = data.data || null;
      
      Logger.info("botStatusSlice.ts : updateWhatsAppConfig()", `Updated configuration: ${JSON.stringify(configData)}`);
      
      return configData;
    } catch (error) {
      Logger.error("botStatusSlice.ts : updateWhatsAppConfig()", `Failed to update configuration: ${error}`);
      return rejectWithValue(error instanceof Error ? error.message : String(error));
    }
  }
);

const botStatusSlice = createSlice({
  name: 'botStatus',
  initialState,
  reducers: {
    clearBotStatus: (state) => {
      state.botStatus = null;
      state.loading = false;
      state.error = null;
      state.lastFetched = null;
      state.whatsappDeducedStatus = 'OFFLINE';
      state.isQRCodeOutdated = false;
      state.emailDeducedStatus = 'OFFLINE';
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch bot status cases
      .addCase(fetchBotStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBotStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.botStatus = action.payload;
        state.lastFetched = Date.now();
        
        // Deduce WhatsApp status based on business rules
        const { status, isOutdated } = deduceWhatsAppStatus(action.payload?.whatsapp || null);
        state.whatsappDeducedStatus = status;
        state.isQRCodeOutdated = isOutdated;
        
        // Deduce Email status based on lastUpdated timestamp
        const emailStatus = deduceEmailStatus(action.payload?.email || null);
        state.emailDeducedStatus = emailStatus;
        
        Logger.info("botStatusSlice.ts : fetchBotStatus.fulfilled", `WhatsApp status deduced: ${status}, isOutdated: ${isOutdated}, Email status: ${emailStatus}`);
      })
      .addCase(fetchBotStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        Logger.error("botStatusSlice.ts : fetchBotStatus.rejected", `Error: ${action.payload}`);
      })
      // Update WhatsApp configuration cases
      .addCase(updateWhatsAppConfig.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateWhatsAppConfig.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        // Update only the whatsapp part of botStatus
        if (state.botStatus) {
          state.botStatus.whatsapp = action.payload;
        } else {
          state.botStatus = {
            whatsapp: action.payload,
            email: null,
            gsmWorkerPool: null
          };
        }
        state.lastFetched = Date.now();
        
        // Deduce WhatsApp status based on business rules
        const { status, isOutdated } = deduceWhatsAppStatus(action.payload);
        state.whatsappDeducedStatus = status;
        state.isQRCodeOutdated = isOutdated;
        
        Logger.info("botStatusSlice.ts : updateWhatsAppConfig.fulfilled", `WhatsApp status deduced: ${status}, isOutdated: ${isOutdated}`);
      })
      .addCase(updateWhatsAppConfig.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        Logger.error("botStatusSlice.ts : updateWhatsAppConfig.rejected", `Error: ${action.payload}`);
      });
  },
});

export const { clearBotStatus, setLoading, setError } = botStatusSlice.actions;
export default botStatusSlice.reducer;
